import { Menu, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/shared/lib/cn';
import { IconButton } from './IconButton';
import { Sidebar, type NavItem, type SidebarProps } from './Sidebar';

export interface AppShellProps {
  items: readonly NavItem[];
  /** Logo lub inna zawartosc nad nawigacja. */
  sidebarHeader?: ReactNode;
  /** Zawartosc na dole paska bocznego (wylogowanie, karta uzytkownika). */
  sidebarFooter?: ReactNode;
  variant?: SidebarProps['variant'];
  /** Pasek widoczny wylacznie na waskich ekranach, obok przycisku menu. */
  mobileHeader?: ReactNode;
  /**
   * Akcje na pasku waskiego ekranu — stoja na lewo od przycisku menu.
   * Osobny slot, zeby powloka nie musiala wiedziec, co dokladnie tam trafia.
   */
  mobileActions?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * Szkielet aplikacji: staly pasek boczny plus obszar tresci.
 *
 * Nawigacja jest jednokolumnowa — pasek podmienia zawartosc zaleznie od obszaru,
 * zamiast rozsuwac obok siebie druga kolumne.
 *
 * Ponizej breakpointu `lg` pasek zamienia sie w szuflade otwierana przyciskiem —
 * makiety pokazuja wylacznie uklad desktopowy, ale aplikacja musi dzialac
 * takze na telefonie.
 */
export function AppShell({
  items,
  sidebarHeader,
  sidebarFooter,
  variant,
  mobileHeader,
  mobileActions,
  children,
  className,
}: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Przejscie na inna trase zamyka szuflade — inaczej zostalaby otwarta
  // nad nowym widokiem. Stan korygujemy w trakcie renderu zamiast efektem:
  // efekt wymusilby dodatkowy przebieg renderowania z otwarta szuflada.
  const [lastPath, setLastPath] = useState(location.pathname);
  if (location.pathname !== lastPath) {
    setLastPath(location.pathname);
    setDrawerOpen(false);
  }

  useEffect(() => {
    if (!drawerOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen]);

  return (
    <div className={cn('bg-canvas flex min-h-dvh', className)}>
      {/* Pasek boczny — na szerokich ekranach zawsze widoczny */}
      <div className="sticky top-0 hidden h-dvh shrink-0 lg:block">
        <Sidebar
          items={items}
          header={sidebarHeader}
          footer={sidebarFooter}
          {...(variant ? { variant } : {})}
        />
      </div>

      {/* Szuflada na waskich ekranach */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Zamknij menu"
            onClick={() => setDrawerOpen(false)}
            className="bg-overlay absolute inset-0"
          />
          <div className="absolute inset-y-0 left-0 flex">
            <Sidebar
              items={items}
              header={sidebarHeader}
              footer={sidebarFooter}
              // W szufladzie zostawiamy pelne etykiety — dymki na dotyku
              // i tak sie nie pokazuja.
              {...(variant ? { variant: variant === 'icons' ? 'labels' : variant } : {})}
              className="shadow-xl"
            />
            <IconButton
              icon={<X />}
              label="Zamknij menu"
              variant="secondary"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 ml-3"
            />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Pasek z przyciskiem menu — tylko na waskich ekranach. Logo po lewej,
            przycisk po prawej. Pasek gasnie ku dolowi: pelne krycie pod logo,
            20% przy dolnej krawedzi. */}
        <div className="border-border from-logo-backdrop to-logo-backdrop-fade flex items-center justify-between gap-3 border-b bg-linear-to-b px-4 py-3 lg:hidden">
          {mobileHeader}
          {/* Akcje i menu trzymamy w jednej grupie po prawej — inaczej
              `justify-between` rozrzucilby je po calej szerokosci paska. */}
          <div className="flex items-center gap-2">
            {mobileActions}
            <IconButton
              icon={<Menu />}
              label="Otwórz menu"
              variant="secondary"
              onClick={() => setDrawerOpen(true)}
            />
          </div>
        </div>

        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}
